import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Chip,
  Card,
  CardContent,
  Grid,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ArrowUpward as UpIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  CreateNewFolder as NewFolderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  DriveFileMove as MoveIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  FolderOpen as FolderOpenIcon,
  Computer as ServerIcon,
  FolderShared as MountIcon,
  Link as LinkIcon,
  Photo as PhotoIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon
} from '@mui/icons-material';
import { HardDrive } from 'lucide-react';
import { Fade, Zoom } from '@mui/material';
import { StatusChipBase, getStatusChipStyles } from '../styles/sharedStyles';

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Root configurations for NAS Drives
const NAS_DRIVE_CONFIGS = [
  {
    id: 'piotrek',
    name: 'NAS Drive Piotrek',
    root: '/home/pp/WAN-Exposed/Piotrek',
    server: '192.168.8.8:/mnt/RAIDZ1_HDD/Data/WAN-Exposed/Piotrek'
  },
  {
    id: 'adam',
    name: 'NAS Drive Adam',
    root: '/home/pp/WAN-Exposed/Adam',
    server: '192.168.8.8:/mnt/RAIDZ1_HDD/Data/WAN-Exposed/Adam'
  }
];

// TrueNAS Logo URL - using local public asset
const TRUENAS_LOGO_URL = '/truenas_logo.png';

const PREVIEW_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.mpo'];
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];

function NASDrive({ apiBaseUrl, token, allowedTabs, visibleSubTabs }) {
  const [activeDriveIndex, setActiveDriveIndex] = useState(() => {
    if (visibleSubTabs && visibleSubTabs.length > 0) {
      return visibleSubTabs[0];
    }
    return 0;
  });
  const activeDrive = NAS_DRIVE_CONFIGS[activeDriveIndex];
  
  const [currentPath, setCurrentPath] = useState(activeDrive.root);
  const [entries, setEntries] = useState([]);
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [nfsStatus, setNfsStatus] = useState({ isMounted: null, loading: true });

  // Handle drive tab change
  const handleDriveChange = (event, newValue) => {
    // If we have filtered tabs, newValue is the index in visibleSubTabs
    const actualIndex = visibleSubTabs ? visibleSubTabs[newValue] : newValue;
    setActiveDriveIndex(actualIndex);
    setCurrentPath(NAS_DRIVE_CONFIGS[actualIndex].root);
    // Reset NFS status when drive changes
    setNfsStatus({ isMounted: null, loading: true });
  };
  
  // Preview states
  const [previewCache, setPreviewCache] = useState({});
  const [previewLoading, setPreviewLoading] = useState({});
  const [fullPreviewEntry, setFullPreviewEntry] = useState(null);
  const [fullPreviewUrl, setFullPreviewUrl] = useState(null);
  const [fullPreviewLoading, setFullPreviewLoading] = useState(false);
  const previewUrlsRef = useRef([]);
  
  // Dialog states
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameEntry, setRenameEntry] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Clipboard for cut/copy operations
  const [clipboard, setClipboard] = useState(null); // { path, type: 'cut' | 'copy' }
  
  const fileInputRef = useRef(null);

  // Fetch directory contents
  const fetchDirectory = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nas-drive/list`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEntries(response.data.entries || []);
        if (response.data.storage_info) {
          setStorageInfo(response.data.storage_info);
        }
      } else {
        setError(response.data.error || 'Failed to list directory');
      }
    } catch (err) {
      console.error('Error fetching directory:', err);
      setError(err.response?.data?.error || 'Failed to fetch directory contents');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchNfsStatus = useCallback(async () => {
    try {
      setNfsStatus(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${apiBaseUrl}/api/nfs/mounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const mounts = response.data.mounts || [];
      const targetMount = mounts.find(mount => mount.mount_point === activeDrive.root);
      setNfsStatus({
        isMounted: targetMount ? targetMount.is_mounted : false,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching NFS mount status:', err);
      setNfsStatus({
        isMounted: false,
        loading: false
      });
    }
  }, [apiBaseUrl, token, activeDrive.root]);

  // Fetch storage info
  const fetchStorageInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nas-drive/storage-info`, {
        params: { path: currentPath },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageInfo(response.data);
    } catch (err) {
      console.error('Error fetching storage info:', err);
    }
  }, [apiBaseUrl, token, currentPath]);

  // Effect to re-fetch directory contents when mount status changes from unmounted to mounted
  const [prevMountStatus, setPrevMountStatus] = useState(null);
  useEffect(() => {
    if (prevMountStatus === false && nfsStatus.isMounted === true) {
      console.log('NFS mount detected, re-fetching directory contents...');
      fetchDirectory(currentPath);
      fetchStorageInfo();
    }
    setPrevMountStatus(nfsStatus.isMounted);
  }, [nfsStatus.isMounted, currentPath, fetchDirectory, fetchStorageInfo]);

  useEffect(() => {
    fetchDirectory(currentPath);
    fetchStorageInfo();
    fetchNfsStatus();
    // NO AUTO-REFRESH for NFS status to prevent HDD spin-up
    // Status is only checked when component mounts or user manually refreshes
  }, [currentPath, fetchDirectory, fetchStorageInfo, fetchNfsStatus]);

  // Sort entries - directories first
  const sortedEntries = React.useMemo(() => {
    return [...entries].sort((a, b) => {
      if (a.type === 'directory' && b.type !== 'directory') return -1;
      if (a.type !== 'directory' && b.type === 'directory') return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
    });
  }, [entries]);

  // Navigation
  const handleNavigate = (entry) => {
    if (entry.type === 'directory') {
      setCurrentPath(entry.path);
    }
  };

  const handleNavigateUp = () => {
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
    setCurrentPath(parentPath || activeDrive.root);
  };

  const handleGoHome = () => {
    setCurrentPath(activeDrive.root);
  };

  const handleRefresh = () => {
    fetchDirectory(currentPath);
    fetchStorageInfo();
  };

  // File operations
  const handleDownload = async (entry) => {
    if (entry.type !== 'file') return;
    
    setOperationLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nas-drive/download`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', entry.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    } finally {
      setOperationLoading(false);
    }
  };

  // New folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setOperationLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/api/nas-drive/folder`, null, {
        params: { path: currentPath, folder_name: newFolderName },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setNewFolderDialogOpen(false);
        setNewFolderName('');
        fetchDirectory(currentPath);
      } else {
        setError(response.data.error || 'Failed to create folder');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.response?.data?.error || 'Failed to create folder');
    } finally {
      setOperationLoading(false);
    }
  };

  // Rename
  const handleRename = async () => {
    if (!renameName.trim() || !renameEntry) return;
    
    setOperationLoading(true);
    try {
      const response = await axios.put(`${apiBaseUrl}/api/nas-drive/rename`, null, {
        params: { path: renameEntry.path, new_name: renameName },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRenameDialogOpen(false);
        setRenameEntry(null);
        setRenameName('');
        fetchDirectory(currentPath);
      } else {
        setError(response.data.error || 'Failed to rename');
      }
    } catch (err) {
      console.error('Error renaming:', err);
      setError(err.response?.data?.error || 'Failed to rename');
    } finally {
      setOperationLoading(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteEntry) return;
    
    setOperationLoading(true);
    try {
      const response = await axios.delete(`${apiBaseUrl}/api/nas-drive/delete`, {
        params: { path: deleteEntry.path },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDeleteDialogOpen(false);
        setDeleteEntry(null);
        fetchDirectory(currentPath);
        fetchStorageInfo();
      } else {
        setError(response.data.error || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      setError(err.response?.data?.error || 'Failed to delete');
    } finally {
      setOperationLoading(false);
    }
  };

  // Upload file
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setOperationLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${apiBaseUrl}/api/nas-drive/upload?path=${encodeURIComponent(currentPath)}`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        fetchDirectory(currentPath);
        fetchStorageInfo();
      } else {
        setError(response.data.error || 'Failed to upload');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      setError(err.response?.data?.error || 'Failed to upload');
    } finally {
      setOperationLoading(false);
    }
  };

  // Copy to clipboard
  const handleCopyToClipboard = (entry, type) => {
    setClipboard({ path: entry.path, type });
  };

  // Paste from clipboard
  const handlePaste = async () => {
    if (!clipboard) return;
    
    setOperationLoading(true);
    try {
      const destName = clipboard.path.split('/').pop();
      const destPath = currentPath + '/' + destName;
      
      let response;
      if (clipboard.type === 'cut') {
        response = await axios.post(`${apiBaseUrl}/api/nas-drive/move`, null, {
          params: { source_path: clipboard.path, dest_path: destPath },
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${apiBaseUrl}/api/nas-drive/copy`, null, {
          params: { source_path: clipboard.path, dest_path: destPath },
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      if (response.data.success) {
        setClipboard(null);
        fetchDirectory(currentPath);
        if (clipboard.type === 'cut') {
          fetchStorageInfo();
        }
      } else {
        setError(response.data.error || 'Failed to paste');
      }
    } catch (err) {
      console.error('Error pasting:', err);
      setError(err.response?.data?.error || 'Failed to paste');
    } finally {
      setOperationLoading(false);
    }
  };

  // Clear clipboard
  const handleClearClipboard = () => {
    setClipboard(null);
  };

  // Parse path into breadcrumbs
  const getBreadcrumbs = () => {
    if (!currentPath.startsWith(activeDrive.root)) {
      return [{ label: 'Restricted', path: currentPath }];
    }
    
    const relativePath = currentPath.slice(activeDrive.root.length);
    const parts = relativePath.split('/').filter(Boolean);
    
    const breadcrumbs = [{ label: `📁 ${activeDrive.name}`, path: activeDrive.root }];
    let pathStr = activeDrive.root;
    
    parts.forEach(part => {
      pathStr += '/' + part;
      breadcrumbs.push({ label: part, path: pathStr });
    });
    
    return breadcrumbs;
  };

  // Preview functions
  const isPreviewable = useCallback((entry) => {
    if (!entry || entry.type !== 'file') return false;
    const name = entry.name?.toLowerCase() || '';
    return PREVIEW_EXTENSIONS.some(ext => name.endsWith(ext));
  }, []);

  const ensurePreview = useCallback(async (entry) => {
    if (!entry || !isPreviewable(entry)) return;
    const existing = previewCache[entry.path];
    if (existing && existing !== '__error') return;
    if (previewLoading[entry.path]) return;

    setPreviewLoading(prev => ({ ...prev, [entry.path]: true }));
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nas-drive/preview`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error('Preview response is not a Blob');
      }

      const url = URL.createObjectURL(blob);
      previewUrlsRef.current.push(url);
      setPreviewCache(prev => ({ ...prev, [entry.path]: url }));
    } catch (err) {
      console.error('Error fetching preview:', err);
      setPreviewCache(prev => ({ ...prev, [entry.path]: '__error' }));
    } finally {
      setPreviewLoading(prev => ({ ...prev, [entry.path]: false }));
    }
  }, [apiBaseUrl, token, isPreviewable, previewCache, previewLoading]);

  const handleFullPreview = async (entry) => {
    if (!isPreviewable(entry)) return;
    setFullPreviewEntry(entry);
    setFullPreviewLoading(true);
    setFullPreviewUrl(null);
    
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nas-drive/preview-full`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      setFullPreviewUrl(url);
    } catch (err) {
      console.error('Error fetching full preview:', err);
      setError('Failed to load high-quality preview');
    } finally {
      setFullPreviewLoading(false);
    }
  };

  const closeFullPreview = () => {
    if (fullPreviewUrl) URL.revokeObjectURL(fullPreviewUrl);
    setFullPreviewEntry(null);
    setFullPreviewUrl(null);
  };

  // Auto-load thumbnails when directory is loaded
  useEffect(() => {
    if (entries.length > 0) {
      entries.forEach((entry) => {
        if (isPreviewable(entry)) {
          ensurePreview(entry);
        }
      });
    }
  }, [entries.length, ensurePreview]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Clear preview cache when path changes
  useEffect(() => {
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setPreviewCache({});
    setPreviewLoading({});
  }, [currentPath]);

  const breadcrumbs = getBreadcrumbs();
  const isAtRoot = currentPath === activeDrive.root;

  return (
    <Box>
      {/* Drive Selector Tabs */}
      <Box sx={{ mb: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Tabs
          value={visibleSubTabs ? visibleSubTabs.indexOf(activeDriveIndex) : activeDriveIndex}
          onChange={handleDriveChange}
          sx={{
            '& .MuiTabs-indicator': {
              background: (theme) => theme.palette.primary.main,
              height: '3px'
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
              '& svg': {
                color: 'inherit'
              }
            }
          }}
        >
          {NAS_DRIVE_CONFIGS
            .filter((config, idx) => !visibleSubTabs || visibleSubTabs.includes(idx))
            .map((config, idx) => (
              <Tab key={config.id} icon={<HardDrive size={18} />} iconPosition="start" label={config.name} />
            ))}
        </Tabs>
      </Box>

      {/* Header with Storage Info */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={TRUENAS_LOGO_URL}
              alt="TrueNAS"
              sx={{
                width: 48,
                height: 48,
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.5))'
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5, letterSpacing: '-0.5px' }}>
                {activeDrive.name} <span style={{ color: '#00d4ff' }}>Explorer</span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <StorageIcon sx={{ fontSize: 14, color: '#00ff88' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                  Read-Write Access | Powered by TrueNAS
                </Typography>
                
                {/* Mount Button */}
                {!nfsStatus.isMounted && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CloudIcon sx={{ fontSize: 14 }} />}
                    onClick={async () => {
                      try {
                        await axios.post(`${apiBaseUrl}/api/nfs/mount`, {
                          mount_point: activeDrive.root
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        // Refresh NFS status
                        await fetchNfsStatus();
                      } catch (err) {
                        console.error('Error mounting NFS share:', err);
                        setError('Failed to mount NFS share');
                      }
                    }}
                    sx={{
                      backgroundColor: (theme) => theme.palette.secondary.main,
                      color: (theme) => theme.palette.secondary.contrastText,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.secondary.main,
                        filter: 'brightness(1.1)'
                      }
                    }}
                  >
                    Mount
                  </Button>
                )}
              </Box>
               {/* Server, Mount Info & NFS Status */}
               <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                 <Tooltip title="TrueNAS Server Path">
                   <Chip
                     icon={<ServerIcon sx={{ fontSize: 14 }} />}
                     label={activeDrive.server}
                     size="small"
                     sx={{
                       backgroundColor: 'rgba(0, 212, 255, 0.1)',
                       border: '1px solid rgba(0, 212, 255, 0.2)',
                       color: 'rgba(255,255,255,0.7)',
                       fontSize: '0.7rem',
                       '& .MuiChip-icon': { color: '#00d4ff' }
                     }}
                   />
                 </Tooltip>
                 <Tooltip title="Local Mount Point">
                   <Chip
                     icon={<MountIcon sx={{ fontSize: 14 }} />}
                     label={activeDrive.root}
                     size="small"
                     sx={{
                       backgroundColor: 'rgba(0, 255, 136, 0.1)',
                       border: '1px solid rgba(0, 255, 136, 0.2)',
                       color: 'rgba(255,255,255,0.7)',
                       fontSize: '0.7rem',
                       '& .MuiChip-icon': { color: '#00ff88' }
                     }}
                   />
                 </Tooltip>
                 <Tooltip title="NFS Mount Status">
                   <Chip
                     icon={nfsStatus.loading ? (
                       <CircularProgress size={14} sx={{ color: '#00d4ff' }} />
                     ) : (
                       nfsStatus.isMounted ? <CloudIcon sx={{ fontSize: 14 }} /> : <CloudOffIcon sx={{ fontSize: 14 }} />
                     )}
                     label={nfsStatus.loading ? 'Loading...' : (nfsStatus.isMounted ? 'Mounted' : 'Unmounted')}
                     size="small"
                     sx={{
                       backgroundColor: nfsStatus.isMounted ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                       border: nfsStatus.isMounted ? '1px solid rgba(0, 255, 136, 0.2)' : '1px solid rgba(255, 68, 68, 0.2)',
                       color: 'rgba(255,255,255,0.7)',
                       fontSize: '0.7rem',
                       '& .MuiChip-icon': { 
                         color: nfsStatus.isMounted ? '#00ff88' : '#ff4444' 
                       }
                     }}
                   />
                 </Tooltip>
               </Box>
            </Box>
          </Box>
          
          {/* Storage Info Card */}
          {storageInfo && (
            <Paper sx={{ 
              p: 2, 
              background: 'rgba(0, 212, 255, 0.08)', 
              border: '1px solid rgba(0, 212, 255, 0.2)',
              borderRadius: '12px',
              minWidth: 280
            }}>
              <Typography variant="subtitle2" sx={{ color: '#00d4ff', mb: 1, fontWeight: 600 }}>
                Storage Information
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Used: {storageInfo.used_human}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Free: {storageInfo.free_human}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={storageInfo.percent_used || 0}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
                      borderRadius: 4
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, display: 'block' }}>
                  {storageInfo.percent_used}% of {storageInfo.total_human} used
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Chip 
                  size="small" 
                  label={`${storageInfo.file_count || 0} files`}
                  sx={{ backgroundColor: 'rgba(0, 212, 255, 0.15)', color: '#fff', fontSize: '0.7rem' }}
                />
                <Chip 
                  size="small" 
                  label={`${storageInfo.folder_count || 0} folders`}
                  sx={{ backgroundColor: 'rgba(0, 255, 136, 0.15)', color: '#fff', fontSize: '0.7rem' }}
                />
              </Stack>
            </Paper>
          )}
        </Box>

        {/* Breadcrumbs and Actions */}
        <Paper sx={{ 
          p: 2, 
          background: 'rgba(255, 255, 255, 0.03)', 
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Home button */}
            <Tooltip title="Go to NAS Drive root">
              <IconButton 
                onClick={handleGoHome}
                sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#00d4ff', backgroundColor: 'rgba(0,212,255,0.1)' } }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>

            {/* Up button */}
            {!isAtRoot && (
              <Tooltip title="Go up one level">
                <IconButton 
                  onClick={handleNavigateUp}
                  sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#00d4ff', backgroundColor: 'rgba(0,212,255,0.1)' } }}
                >
                  <UpIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Refresh */}
            <Tooltip title="Refresh">
              <IconButton 
                onClick={handleRefresh}
                sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#00ff88', backgroundColor: 'rgba(0,255,136,0.1)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            {/* Clipboard status */}
            {clipboard && (
              <Chip
                icon={clipboard.type === 'cut' ? <MoveIcon /> : <CopyIcon />}
                label={clipboard.type === 'cut' ? 'Move ready' : 'Copy ready'}
                onDelete={handleClearClipboard}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255, 193, 7, 0.15)', 
                  color: '#ffc107',
                  '& .MuiChip-icon': { color: '#ffc107' }
                }}
              />
            )}

            {/* Paste button */}
            {clipboard && (
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudDownloadIcon />}
                onClick={handlePaste}
                disabled={operationLoading}
                sx={{ 
                  backgroundColor: (theme) => theme.palette.secondary.main,
                  color: (theme) => theme.palette.secondary.contrastText,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.secondary.main,
                    filter: 'brightness(1.1)'
                  }
                }}
              >
                Paste Here
              </Button>
            )}

            {/* Upload button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={operationLoading}
            sx={{ 
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.main,
                filter: 'brightness(1.1)'
              }
            }}
            >
              Upload
            </Button>

            {/* New folder button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<NewFolderIcon />}
              onClick={() => setNewFolderDialogOpen(true)}
              disabled={operationLoading}
              sx={{ 
                background: 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
                borderRadius: '8px',
                textTransform: 'none'
              }}
            >
              New Folder
            </Button>
          </Box>

          {/* Breadcrumbs */}
          <Box sx={{ mt: 2 }}>
            <Breadcrumbs sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {breadcrumbs.map((crumb, index) => (
                <Typography 
                  key={index}
                  component="span"
                  onClick={() => index < breadcrumbs.length - 1 && setCurrentPath(crumb.path)}
                  sx={{ 
                    color: index === breadcrumbs.length - 1 ? '#fff' : 'rgba(255,255,255,0.5)',
                    cursor: index < breadcrumbs.length - 1 ? 'pointer' : 'default',
                    fontSize: '0.875rem',
                    '&:hover': index < breadcrumbs.length - 1 ? { color: '#00d4ff' } : {}
                  }}
                >
                  {crumb.label}
                </Typography>
              ))}
            </Breadcrumbs>
          </Box>
        </Paper>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ 
            mb: 2, 
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            '& .MuiAlert-message': { color: '#ff8888' }
          }}
        >
          {error}
        </Alert>
      )}

      {/* File Table */}
      <TableContainer 
        component={Paper}
        sx={{ 
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {loading && (
          <LinearProgress sx={{ backgroundColor: 'rgba(0,212,255,0.1)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #00d4ff, #00ff88)' }}} />
        )}
        
         <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
              {!isAtRoot && (
                <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>Preview</TableCell>
              )}
              <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>Name</TableCell>
              <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>Size</TableCell>
              <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={isAtRoot ? 3 : 4} sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.4)' }}>
                  This folder is empty
                </TableCell>
              </TableRow>
            )}
            {sortedEntries.map((entry, index) => (
              <TableRow 
                key={entry.path}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                  animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`
                }}
              >
                {!isAtRoot && (
                  <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)', width: 80 }}>
                    {isPreviewable(entry) && (
                      <Box sx={{ 
                        width: 60, 
                        height: 60, 
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        '&:hover': { 
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)'
                        },
                        transition: 'all 0.2s ease'
                      }}>
                        {previewLoading[entry.path] ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            width: '100%', 
                            height: '100%',
                            background: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <CircularProgress size={24} sx={{ color: '#00d4ff' }} />
                          </Box>
                        ) : previewCache[entry.path] && previewCache[entry.path] !== '__error' ? (
                          <img 
                            src={previewCache[entry.path]} 
                            alt={entry.name}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover' 
                            }}
                            onLoad={() => {}}
                            onError={(e) => {
                              console.error('Image load error');
                              setPreviewCache(prev => ({ ...prev, [entry.path]: '__error' }));
                            }}
                            onClick={() => handleFullPreview(entry)}
                          />
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: '100%', 
                              height: '100%',
                              background: 'rgba(255, 255, 255, 0.05)',
                              '&:hover': { background: 'rgba(255, 255, 255, 0.08)' }
                            }}
                            onClick={() => ensurePreview(entry)}
                          >
                            <PhotoIcon sx={{ color: 'rgba(255, 255, 255, 0.3)' }} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {entry.type === 'directory' ? (
                      <FolderIcon sx={{ color: '#ffc107', fontSize: 28 }} />
                    ) : isPreviewable(entry) ? (
                      <PhotoIcon sx={{ color: '#00d4ff', fontSize: 28 }} />
                    ) : (
                      <FileIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 28 }} />
                    )}
                    <Typography 
                      sx={{ 
                        color: '#fff', 
                        cursor: 'pointer',
                        '&:hover': { color: '#00d4ff' }
                      }}
                      onClick={() => entry.type === 'directory' ? handleNavigate(entry) : isPreviewable(entry) ? handleFullPreview(entry) : {}}
                    >
                      {entry.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {entry.type === 'directory' ? '—' : formatBytes(entry.size)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Download */}
                    {entry.type === 'file' && (
                      <Tooltip title="Download">
                        <IconButton 
                          size="small"
                          onClick={() => handleDownload(entry)}
                          sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00d4ff', backgroundColor: 'rgba(0,212,255,0.1)' } }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* Rename */}
                    <Tooltip title="Rename">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setRenameEntry(entry);
                          setRenameName(entry.name);
                          setRenameDialogOpen(true);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00d4ff', backgroundColor: 'rgba(0,212,255,0.1)' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Copy */}
                    <Tooltip title="Copy to clipboard">
                      <IconButton 
                        size="small"
                        onClick={() => handleCopyToClipboard(entry, 'copy')}
                        sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00ff88', backgroundColor: 'rgba(0,255,136,0.1)' } }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Cut */}
                    <Tooltip title="Cut to clipboard">
                      <IconButton 
                        size="small"
                        onClick={() => handleCopyToClipboard(entry, 'cut')}
                        sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff9800', backgroundColor: 'rgba(255,152,0,0.1)' } }}
                      >
                        <MoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Delete */}
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setDeleteEntry(entry);
                          setDeleteDialogOpen(true);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff4444', backgroundColor: 'rgba(255,68,68,0.1)' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New Folder Dialog */}
      <Dialog 
        open={newFolderDialogOpen} 
        onClose={() => setNewFolderDialogOpen(false)}
        PaperProps={{
          sx: { 
            background: 'linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.95) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          Create New Folder
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          <Button onClick={() => setNewFolderDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateFolder} 
            variant="contained"
            disabled={!newFolderName.trim() || operationLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
              borderRadius: '8px'
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog 
        open={renameDialogOpen} 
        onClose={() => setRenameDialogOpen(false)}
        PaperProps={{
          sx: { 
            background: 'linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.95) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          Rename
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="New Name"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          <Button onClick={() => setRenameDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleRename} 
            variant="contained"
            disabled={!renameName.trim() || operationLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
              borderRadius: '8px'
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { 
            background: 'linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.95) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Are you sure you want to delete "{deleteEntry?.name}"? 
            {deleteEntry?.type === 'directory' && ' This will delete all contents inside.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained"
            color="error"
            disabled={operationLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #ff4444 0%, #cc0000 100%)',
              borderRadius: '8px'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => {
          setUploadDialogOpen(false);
          setSelectedFile(null);
        }}
        PaperProps={{
          sx: { 
            background: 'linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.95) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          Upload File
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
          <Button
            fullWidth
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{ 
              py: 2,
              color: 'rgba(255,255,255,0.6)',
              borderColor: 'rgba(255,255,255,0.2)',
              borderStyle: 'dashed',
              borderRadius: '12px',
              '&:hover': {
                borderColor: '#00d4ff',
                backgroundColor: 'rgba(0,212,255,0.05)'
              }
            }}
          >
            {selectedFile ? selectedFile.name : 'Choose a file...'}
          </Button>
          {selectedFile && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, display: 'block' }}>
              Size: {formatBytes(selectedFile.size)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFile(null);
          }} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained"
            disabled={!selectedFile || operationLoading}
            sx={{ 
              background: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
              color: '#000',
              borderRadius: '8px',
              fontWeight: 600
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Preview Dialog */}
      <Dialog
        open={!!fullPreviewEntry}
        onClose={closeFullPreview}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ZoomInIcon sx={{ color: '#00d4ff' }} />
            <Typography variant="h6">{fullPreviewEntry?.name}</Typography>
          </Box>
          <IconButton 
            onClick={closeFullPreview}
            sx={{ color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#fff' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fullPreviewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={60} sx={{ color: '#00d4ff' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Loading high-quality preview...
              </Typography>
            </Box>
          ) : fullPreviewUrl ? (
            <Box sx={{ 
              maxWidth: '100%', 
              maxHeight: '80vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img 
                src={fullPreviewUrl} 
                alt={fullPreviewEntry?.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '80vh', 
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              <Typography variant="body1">Failed to load preview</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-start'
        }}>
          <Button 
            onClick={() => handleDownload(fullPreviewEntry)}
            startIcon={<DownloadIcon />}
            disabled={operationLoading}
            sx={{ 
              color: '#00d4ff',
              textTransform: 'none'
            }}
          >
            Download Original
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operation Loading Overlay */}
      {operationLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </Box>
      )}
    </Box>
  );
}

export default NASDrive;
